// src/types/cards.ts
function isNumberCard(card) {
  return card.type === "number";
}
function isModifierCard(card) {
  return card.type === "modifier";
}
function isActionCard(card) {
  return card.type === "action";
}
function isNegativeModifier(card) {
  return card.type === "modifier" && (card.value.startsWith("-") || card.value === "div2");
}
function isVengeanceCard(card) {
  if (card.type === "number" && "special" in card && card.special) return true;
  if (card.type === "modifier" && isNegativeModifier(card)) return true;
  if (card.type === "action") {
    const vengeanceActions = ["just-one-more", "swap", "steal", "discard", "flip-four"];
    return vengeanceActions.includes(card.value);
  }
  return false;
}

// src/constants/events.ts
var EVENTS = {
  CREATE_ROOM: "createRoom",
  JOIN_ROOM: "joinRoom",
  LEAVE_ROOM: "leaveRoom",
  START_GAME: "startGame",
  ADD_CARD: "addCard",
  REMOVE_CARD: "removeCard",
  STAY: "stay",
  BUST: "bust",
  NEXT_PLAYER: "nextPlayer",
  UNDO: "undo",
  END_ROUND: "endRound",
  NEXT_ROUND: "nextRound",
  REORDER_PLAYERS: "reorderPlayers",
  REMOVE_PLAYER: "removePlayer",
  ADD_PLAYER: "addPlayer",
  PLAY_AGAIN: "playAgain",
  RECONNECT: "reconnect",
  // Digital mode events
  HIT: "hit",
  CHOOSE_TARGET: "chooseTarget",
  // Vengeance action events
  SELECT_SWAP_CARDS: "selectSwapCards",
  SELECT_STEAL_TARGET: "selectStealTarget",
  SELECT_DISCARD_TARGET: "selectDiscardTarget",
  FORFEIT_ACTION: "forfeitAction",
  // Social events
  SEND_REACTION: "sendReaction",
  SEND_VOICE_MEMO: "sendVoiceMemo",
  SEND_SOUNDBITE: "sendSoundbite",
  REACTION: "reaction",
  VOICE_MEMO: "voiceMemo",
  SOUNDBITE: "soundbite",
  // Group events
  JOIN_GROUP: "joinGroup",
  // Presence events
  PRESENCE_UPDATE: "presenceUpdate",
  PRESENCE_BULK: "presenceBulk",
  REQUEST_PRESENCE: "requestPresence",
  // Invite events
  SEND_GAME_INVITE: "sendGameInvite",
  GAME_INVITE: "gameInvite",
  RESPOND_INVITE: "respondInvite",
  INVITE_EXPIRED: "inviteExpired",
  SEND_GROUP_INVITE: "sendGroupInvite",
  GROUP_INVITE: "groupInvite",
  // Friend notification events
  FRIEND_REQUEST: "friendRequest",
  FRIEND_ACCEPTED: "friendAccepted",
  // Spectator events
  SPECTATE_ROOM: "spectateRoom",
  SPECTATE_SUCCESS: "spectateSuccess",
  TOGGLE_WANT_TO_PLAY: "toggleWantToPlay",
  KICK_SPECTATOR: "kickSpectator",
  // Betting events
  PLACE_BET: "placeBet",
  BETS_UPDATE: "betsUpdate",
  // Chat events
  SEND_CHAT: "sendChat",
  CHAT_MESSAGE: "chatMessage",
  CHAT_HISTORY: "chatHistory",
  // Challenge / Story events
  START_CHALLENGE: "startChallenge",
  CHALLENGE_COMPLETE: "challengeComplete",
  START_DAILY_CHALLENGE: "startDailyChallenge",
  DAILY_CHALLENGE_COMPLETE: "dailyChallengeComplete",
  // Achievement events
  ACHIEVEMENT_UNLOCKED: "achievementUnlocked",
  COINS_EARNED: "coinsEarned",
  // Shiny card events
  SHINY_UNLOCK: "shinyUnlock",
  // Duel events
  DUEL_CHALLENGE: "duelChallenge",
  DUEL_CHALLENGE_ACCEPT: "duelChallengeAccept",
  DUEL_CHALLENGE_DECLINE: "duelChallengeDecline",
  DUEL_WAGER_SUBMIT: "duelWagerSubmit",
  DUEL_LOADOUT_SUBMIT: "duelLoadoutSubmit",
  DUEL_ACTION_PLAY: "duelActionPlay",
  DUEL_PRIVATE_STATE: "duelPrivateState",
  DUEL_RESULT: "duelResult",
  // Hot Streak events (client → server)
  HOT_STREAK_START_GAME: "hotStreakStartGame",
  HOT_STREAK_DRAFT_PICK: "hotStreakDraftPick",
  HOT_STREAK_SUBMIT_CARD: "hotStreakSubmitCard",
  HOT_STREAK_DOUBLE_DOWN: "hotStreakDoubleDown",
  // Hot Streak events (server → client)
  HOT_STREAK_STATE: "hotStreakState",
  HOT_STREAK_PRIVATE: "hotStreakPrivate",
  HOT_STREAK_RACE_CARD: "hotStreakRaceCard",
  HOT_STREAK_RACE_FOLD: "hotStreakRaceFold",
  HOT_STREAK_RACE_FINISH: "hotStreakRaceFinish",
  HOT_STREAK_RACE_END: "hotStreakRaceEnd",
  // Server → Client events
  ROOM_CREATED: "roomCreated",
  GAME_STATE: "gameState",
  JOIN_SUCCESS: "joinSuccess",
  ERROR: "error",
  PLAYER_DISCONNECTED: "playerDisconnected",
  PLAYER_RECONNECTED: "playerReconnected",
  SESSION_REPLACED: "sessionReplaced"
};

// src/constants/cards.ts
var NUMBER_CARD_VALUES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
var MODIFIER_CARD_VALUES = ["+2", "+4", "+6", "+8", "+10", "x2"];
var ACTION_CARD_VALUES = ["flip-three", "freeze", "second-chance"];
var VENGEANCE_ACTION_CARD_VALUES = ["just-one-more", "swap", "steal", "discard", "flip-four"];
var VENGEANCE_MODIFIER_CARD_VALUES = ["div2", "-2", "-4", "-6", "-8", "-10"];
var NUMBER_CARD_COUNTS = {
  0: 1,
  1: 1,
  2: 2,
  3: 3,
  4: 4,
  5: 5,
  6: 6,
  7: 7,
  8: 8,
  9: 9,
  10: 10,
  11: 11,
  12: 12,
  13: 0
  // Lucky 13 only in Vengeance (added via VENGEANCE_SPECIAL_NUMBER_CARDS)
};
var NUMBER_CARD_COLORS = {
  0: "#800020",
  // Maroon
  1: "#CC0066",
  // Magenta
  2: "#E8768E",
  // Pink
  3: "#DAA520",
  // Yellow/Gold
  4: "#7CB342",
  // Lime Green
  5: "#26A69A",
  // Teal
  6: "#7B1FA2",
  // Purple
  7: "#2E7D32",
  // Dark Green
  8: "#E65100",
  // Orange
  9: "#D32F2F",
  // Red
  10: "#B71C1C",
  // Crimson
  11: "#1A237E",
  // Navy
  12: "#1B5E20",
  // Forest Green
  13: "#2E7D32"
  // Green (Lucky 13)
};
var NUMBER_CARD_NAMES = {
  0: "ZERO",
  1: "ONE",
  2: "TWO",
  3: "THREE",
  4: "FOUR",
  5: "FIVE",
  6: "SIX",
  7: "SEVEN",
  8: "EIGHT",
  9: "NINE",
  10: "TEN",
  11: "ELEVEN",
  12: "TWELVE",
  13: "THIRTEEN"
};
var MODIFIER_CARD_COLOR = "#E65100";
var ACTION_CARD_COLORS = {
  "flip-three": "#FDD835",
  // Yellow
  "freeze": "#4FC3F7",
  // Light Blue
  "second-chance": "#E53935",
  // Red
  "just-one-more": "#AB47BC",
  // Purple
  "swap": "#FF7043",
  // Deep Orange
  "steal": "#66BB6A",
  // Green
  "discard": "#EF5350",
  // Red
  "flip-four": "#FFCA28"
  // Amber
};
var ACTION_CARD_ICONS = {
  "flip-three": "lightning",
  "freeze": "padlock",
  "second-chance": "heart",
  "just-one-more": "hand-stop",
  "swap": "arrows-swap",
  "steal": "hand-grab",
  "discard": "trash",
  "flip-four": "lightning-bolt"
};
var NEGATIVE_MODIFIER_COLOR = "#C62828";
var DIV2_MODIFIER_COLOR = "#5C6BC0";
var MODIFIER_CARD_COUNTS = {
  "+2": 1,
  "+4": 1,
  "+6": 1,
  "+8": 1,
  "+10": 1,
  "x2": 1,
  // Vengeance modifiers (0 in standard deck, added via VENGEANCE_MODIFIER_CARD_COUNTS)
  "div2": 0,
  "-2": 0,
  "-4": 0,
  "-6": 0,
  "-8": 0,
  "-10": 0
};
var ACTION_CARD_COUNTS = {
  "freeze": 3,
  "flip-three": 3,
  "second-chance": 3,
  // Vengeance cards (0 in standard deck, added via VENGEANCE_ counts)
  "just-one-more": 0,
  "swap": 0,
  "steal": 0,
  "discard": 0,
  "flip-four": 0
};
var TOTAL_DECK_SIZE = 94;
var VENGEANCE_SPECIAL_NUMBER_CARDS = [
  { value: 7, special: "unlucky7", count: 1 },
  { value: 13, special: "lucky13", count: 1 }
];
var VENGEANCE_ACTION_CARD_COUNTS = {
  "just-one-more": 2,
  "swap": 2,
  "steal": 2,
  "discard": 2,
  "flip-four": 2
};
var VENGEANCE_MODIFIER_CARD_COUNTS = {
  "div2": 1,
  "-2": 1,
  "-4": 1,
  "-6": 1,
  "-8": 1,
  "-10": 1
};
var VENGEANCE_DECK_SIZE = TOTAL_DECK_SIZE + 18;
var DEAL_DELAY_MS = 500;
var REVEAL_DELAY_MS = 900;
var FLIP_7_BONUS = 15;
var FLIP_7_THRESHOLD = 7;
var DEFAULT_TARGET_SCORE = 200;
var MAX_PLAYERS = 10;
var MAX_SPECTATORS = 5;
var MIN_PLAYERS = 1;
var PLAYER_NAME_MAX_LENGTH = 20;
var TARGET_SCORE_MIN = 50;
var TARGET_SCORE_MAX = 1e3;
var ROOM_EXPIRY_MS = 2 * 60 * 60 * 1e3;
var TABLE_TIERS = {
  free: { entryFee: 0, winReward: 10 },
  bronze: { entryFee: 50, winReward: 0 },
  silver: { entryFee: 200, winReward: 0 },
  gold: { entryFee: 500, winReward: 0 }
};
var BOT_NAMES = ["Alice", "Bob", "Charlie"];
var BOT_DECISION_DELAY_MIN = 500;
var BOT_DECISION_DELAY_MAX = 1500;
var MAX_BOTS = 3;
var SPECTATOR_ONLY_EXPIRY_MS = 15 * 60 * 1e3;
var GROUP_SLUG_MAX_LENGTH = 30;
var GROUP_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/;
var CHAT_MAX_LENGTH = 200;
var CHAT_RATE_LIMIT_MS = 1e3;
var CHAT_HISTORY_SIZE = 50;

// src/constants/reactions.ts
var DEFAULT_REACTION_PRESETS = {
  aaaaaiiiii: { label: "Aaaaaiiiii", icon: "\u{1F624}" },
  doerustig: { label: "Doe rustig", icon: "\u{1F9D8}" },
  conjobomama: { label: "Conjo bo mama", icon: "\u{1F525}" },
  nice: { label: "Nice!", icon: "\u{1F44D}" },
  ouch: { label: "Ouch!", icon: "\u{1F62C}" },
  risky: { label: "Risky!", icon: "\u{1F3B2}" },
  noway: { label: "No way!", icon: "\u{1F631}" },
  gg: { label: "GG", icon: "\u{1F91D}" },
  lucky: { label: "Lucky!", icon: "\u{1F340}" },
  bust: { label: "Bust incoming", icon: "\u{1F4A5}" }
};
var MAX_VOICE_MEMO_SIZE = 1e5;
var VOICE_MEMO_MAX_SECONDS = 5;
var REACTION_COOLDOWN_MS = 1500;
var REACTION_TOAST_DURATION_MS = 4e3;
var MAX_VISIBLE_TOASTS = 3;

// src/constants/soundbites.ts
var SOUNDBITE_PACKS = ["Classic", "Gino Pietermaai", "The Champ", "Arie & Bastiaan Vol. 1", "Arie & Bastiaan Vol. 2", "Arie & Bastiaan Vol. 3", "Arie & Bastiaan Classics", "Zeebats"];
var DEFAULT_SOUNDBITE_PRESETS = {
  // ── Classic ──
  letsgo: {
    label: "Let's Go",
    icon: "\u{1F680}",
    url: "/sounds/letsgo.mp3",
    pack: "Classic"
  },
  ikwilkaas: {
    label: "Ik Wil Kaas",
    icon: "\u{1F9C0}",
    url: "/sounds/ikwilkaas.mp3",
    pack: "Classic"
  },
  manmanman: {
    label: "Man Man Man",
    icon: "\u{1F926}",
    url: "/sounds/manmanman.mp3",
    pack: "Classic"
  },
  koekoek: {
    label: "Koekoek",
    icon: "\u{1F426}",
    url: "/sounds/koekoek.mp3",
    pack: "Classic"
  },
  denketniet: {
    label: "Denk t Niet",
    icon: "\u{1F645}",
    url: "/sounds/denketniet.mp3",
    pack: "Classic"
  },
  balie: {
    label: "Balie",
    icon: "\u{1F44A}",
    url: "/sounds/balie.mp3",
    pack: "Classic"
  },
  knettah: {
    label: "Knettah",
    icon: "\u{1F92A}",
    url: "/sounds/knettah.mp3",
    pack: "Classic"
  },
  heerlijk: {
    label: "Heerlijk",
    icon: "\u{1F60B}",
    url: "/sounds/heerlijk.mp3",
    pack: "Classic"
  },
  rustaaagh: {
    label: "Rustaaagh",
    icon: "\u{1F92C}",
    url: "/sounds/rustaaagh.mp3",
    pack: "Classic"
  },
  klootzak: {
    label: "Klootzak",
    icon: "\u{1F621}",
    url: "/sounds/klootzak.mp3",
    pack: "Classic"
  },
  opjemuil: {
    label: "Op Je Muil",
    icon: "\u{1F4B5}",
    url: "/sounds/opjemuil.mp3",
    pack: "Classic"
  },
  kukeleku: {
    label: "Kukeleku",
    icon: "\u{1F413}",
    url: "/sounds/kukeleku.mp3",
    pack: "Classic"
  },
  vergiftigen: {
    label: "Vergiftigen",
    icon: "\u{1F4A7}",
    url: "/sounds/vergiftigen.mp3",
    pack: "Classic"
  },
  datzieje: {
    label: "Dat Zie Je",
    icon: "\u{1F624}",
    url: "/sounds/datzieje.mp3",
    pack: "Classic"
  },
  fietsopa: {
    label: "Fietsopa",
    icon: "\u{1F6B2}",
    url: "/sounds/fietsopa.mp3",
    pack: "Classic"
  },
  heswa: {
    label: "He swa",
    icon: "\u{1F3B5}",
    url: "/sounds/heswa.mp3",
    pack: "Classic"
  },
  // ── Gino Pietermaai ──
  "gino-grappig": {
    label: "Grappig?",
    icon: "\u{1F928}",
    url: "/sounds/gino-grappig.mp3",
    pack: "Gino Pietermaai"
  },
  "gino-ogendicht": {
    label: "Ogen Dicht",
    icon: "\u{1F634}",
    url: "/sounds/gino-ogendicht.mp3",
    pack: "Gino Pietermaai"
  },
  "gino-scrabble": {
    label: "Scrabble?",
    icon: "\u{1F3B2}",
    url: "/sounds/gino-scrabble.mp3",
    pack: "Gino Pietermaai"
  },
  "gino-tips": {
    label: "Mijn Tips",
    icon: "\u{1F4A1}",
    url: "/sounds/gino-tips.mp3",
    pack: "Gino Pietermaai"
  },
  "gino-brief": {
    label: "Die Brief",
    icon: "\u2709\uFE0F",
    url: "/sounds/gino-brief.mp3",
    pack: "Gino Pietermaai"
  },
  "gino-goud": {
    label: "GOUD!",
    icon: "\u{1F947}",
    url: "/sounds/gino-goud.mp3",
    pack: "Gino Pietermaai"
  },
  "gino-sukkel": {
    label: "Hey Sukkel",
    icon: "\u{1F61C}",
    url: "/sounds/gino-sukkel.mp3",
    pack: "Gino Pietermaai"
  },
  "gino-ookogendicht": {
    label: "Ook Ogen Dicht",
    icon: "\u{1F644}",
    url: "/sounds/gino-ookogendicht.mp3",
    pack: "Gino Pietermaai"
  },
  "gino-gaanwerken": {
    label: "Gaan Werken",
    icon: "\u{1F4AA}",
    url: "/sounds/gino-gaanwerken.mp3",
    pack: "Gino Pietermaai"
  },
  "gino-weetniethoe": {
    label: "Weet Niet Hoe",
    icon: "\u{1F937}",
    url: "/sounds/gino-weetniethoe.mp3",
    pack: "Gino Pietermaai"
  },
  "gino-kip": {
    label: "Kip",
    icon: "\u{1F414}",
    url: "/sounds/gino-kip.mp3",
    pack: "Gino Pietermaai"
  },
  "gino-kokosolie": {
    label: "Kokosolie",
    icon: "\u{1F965}",
    url: "/sounds/gino-kokosolie.mp3",
    pack: "Gino Pietermaai"
  },
  "gino-neeswa": {
    label: "Nee Swa",
    icon: "\u{1F645}",
    url: "/sounds/gino-neeswa.mp3",
    pack: "Gino Pietermaai"
  },
  "gino-pijpen": {
    label: "Pijpen",
    icon: "\u{1F60F}",
    url: "/sounds/gino-pijpen.mp3",
    pack: "Gino Pietermaai"
  },
  "gino-rustuit": {
    label: "Rust Uit",
    icon: "\u{1F6CC}",
    url: "/sounds/gino-rustuit.mp3",
    pack: "Gino Pietermaai"
  },
  "gino-handopkip": {
    label: "Hand Op De Kip",
    icon: "\u270B",
    url: "/sounds/gino-handopkip.mp3",
    pack: "Gino Pietermaai"
  },
  "gino-zoklein": {
    label: "Zo Klein",
    icon: "\u{1F90F}",
    url: "/sounds/gino-zoklein.mp3",
    pack: "Gino Pietermaai"
  },
  // ── The Champ ──
  "champ-aiiijohnny": {
    label: "Aiiii Johnny",
    icon: "\u{1F64C}",
    url: "/sounds/champ-aiiijohnny.mp3",
    pack: "The Champ"
  },
  "champ-comeon": {
    label: "Come On!",
    icon: "\u{1F4AA}",
    url: "/sounds/champ-comeon.mp3",
    pack: "The Champ"
  },
  "champ-donthear": {
    label: "Don't Hear You",
    icon: "\u{1F9BB}",
    url: "/sounds/champ-donthear.mp3",
    pack: "The Champ"
  },
  "champ-itsthechamp": {
    label: "It's The Champ",
    icon: "\u{1F3C6}",
    url: "/sounds/champ-itsthechamp.mp3",
    pack: "The Champ"
  },
  "champ-ready": {
    label: "Johnny Is Ready",
    icon: "\u{1F60E}",
    url: "/sounds/champ-ready.mp3",
    pack: "The Champ"
  },
  "champ-trucoftheweek": {
    label: "Truc Of The Week",
    icon: "\u2728",
    url: "/sounds/champ-trucoftheweek.mp3",
    pack: "The Champ"
  },
  // ── Arie & Bastiaan Vol. 1 ──
  "ab1-sticky": { label: "Sticky Roken", icon: "\u{1F6AC}", url: "/sounds/ab1-sticky.mp3", pack: "Arie & Bastiaan Vol. 1" },
  "ab1-godverdomme": { label: "Godverdomme!", icon: "\u{1F92C}", url: "/sounds/ab1-godverdomme.mp3", pack: "Arie & Bastiaan Vol. 1" },
  "ab1-haaahaaa": { label: "HAAAHAAA", icon: "\u{1F602}", url: "/sounds/ab1-haaahaaa.mp3", pack: "Arie & Bastiaan Vol. 1" },
  "ab1-heuyarie": { label: "Heeuy Arie", icon: "\u{1F44B}", url: "/sounds/ab1-heuyarie.mp3", pack: "Arie & Bastiaan Vol. 1" },
  "ab1-houdjebek": { label: "Houd Je Bek", icon: "\u{1F92B}", url: "/sounds/ab1-houdjebek.mp3", pack: "Arie & Bastiaan Vol. 1" },
  "ab1-mazzel": { label: "Mazzel", icon: "\u270C\uFE0F", url: "/sounds/ab1-mazzel.mp3", pack: "Arie & Bastiaan Vol. 1" },
  "ab1-neearie": { label: "Neee Arie", icon: "\u{1F645}", url: "/sounds/ab1-neearie.mp3", pack: "Arie & Bastiaan Vol. 1" },
  "ab1-wtf": { label: "WTF", icon: "\u{1F633}", url: "/sounds/ab1-wtf.mp3", pack: "Arie & Bastiaan Vol. 1" },
  // ── Arie & Bastiaan Vol. 2 ──
  "ab2-goedidee": { label: "Goed Idee!", icon: "\u{1F4A1}", url: "/sounds/ab2-goedidee.mp3", pack: "Arie & Bastiaan Vol. 2" },
  "ab2-harrywtf": { label: "Harry WTF", icon: "\u{1F631}", url: "/sounds/ab2-harrywtf.mp3", pack: "Arie & Bastiaan Vol. 2" },
  "ab2-kutkoters": { label: "Kut Koters!", icon: "\u{1F624}", url: "/sounds/ab2-kutkoters.mp3", pack: "Arie & Bastiaan Vol. 2" },
  "ab2-jekoffer": { label: "Je Koffer", icon: "\u{1F9F3}", url: "/sounds/ab2-jekoffer.mp3", pack: "Arie & Bastiaan Vol. 2" },
  "ab2-jedoetmaar": { label: "Je Doet Maar", icon: "\u{1F937}", url: "/sounds/ab2-jedoetmaar.mp3", pack: "Arie & Bastiaan Vol. 2" },
  "ab2-trippen": { label: "Lekker Trippen", icon: "\u{1F344}", url: "/sounds/ab2-trippen.mp3", pack: "Arie & Bastiaan Vol. 2" },
  "ab2-pompen": { label: "Pompen Maar", icon: "\u{1F4AA}", url: "/sounds/ab2-pompen.mp3", pack: "Arie & Bastiaan Vol. 2" },
  "ab2-wahhaaa": { label: "Wahhhaaaa", icon: "\u{1F62D}", url: "/sounds/ab2-wahhaaa.mp3", pack: "Arie & Bastiaan Vol. 2" },
  // ── Arie & Bastiaan Vol. 3 ──
  "ab3-allebei": { label: "Allebei", icon: "\u270B", url: "/sounds/ab3-allebei.mp3", pack: "Arie & Bastiaan Vol. 3" },
  "ab3-achteruit": { label: "Achteruit", icon: "\u{1F697}", url: "/sounds/ab3-achteruit.mp3", pack: "Arie & Bastiaan Vol. 3" },
  "ab3-50cent": { label: "50 Cent", icon: "\u{1F3B5}", url: "/sounds/ab3-50cent.mp3", pack: "Arie & Bastiaan Vol. 3" },
  "ab3-clown": { label: "Kom Mee Clown", icon: "\u{1F921}", url: "/sounds/ab3-clown.mp3", pack: "Arie & Bastiaan Vol. 3" },
  "ab3-subwoofer": { label: "Subwoofer", icon: "\u{1F50A}", url: "/sounds/ab3-subwoofer.mp3", pack: "Arie & Bastiaan Vol. 3" },
  "ab3-watdoejij": { label: "Wat Doe Jij", icon: "\u{1F9D0}", url: "/sounds/ab3-watdoejij.mp3", pack: "Arie & Bastiaan Vol. 3" },
  "ab3-handje": { label: "Aan Het Handje", icon: "\u270B", url: "/sounds/ab3-handje.mp3", pack: "Arie & Bastiaan Vol. 3" },
  "ab3-wtfheuh": { label: "WTF Heuh", icon: "\u{1F612}", url: "/sounds/ab3-wtfheuh.mp3", pack: "Arie & Bastiaan Vol. 3" },
  // ── Arie & Bastiaan Classics ──
  "abc-flikkers": { label: "Twee Flikkers", icon: "\u{1F308}", url: "/sounds/abc-flikkers.mp3", pack: "Arie & Bastiaan Classics" },
  "abc-aight": { label: "Haha Aight", icon: "\u{1F604}", url: "/sounds/abc-aight.mp3", pack: "Arie & Bastiaan Classics" },
  "abc-hehehe1": { label: "Hehehe 1", icon: "\u{1F606}", url: "/sounds/abc-hehehe1.mp3", pack: "Arie & Bastiaan Classics" },
  "abc-hehehe2": { label: "Hehehe 2", icon: "\u{1F606}", url: "/sounds/abc-hehehe2.mp3", pack: "Arie & Bastiaan Classics" },
  "abc-hehehe3": { label: "Hehehe 3", icon: "\u{1F606}", url: "/sounds/abc-hehehe3.mp3", pack: "Arie & Bastiaan Classics" },
  "abc-hehehe4": { label: "Hehehe 4", icon: "\u{1F606}", url: "/sounds/abc-hehehe4.mp3", pack: "Arie & Bastiaan Classics" },
  "abc-hehehe5": { label: "Hehehe 5", icon: "\u{1F606}", url: "/sounds/abc-hehehe5.mp3", pack: "Arie & Bastiaan Classics" },
  "abc-hoeren": { label: "Naar De Hoeren", icon: "\u{1F4B8}", url: "/sounds/abc-hoeren.mp3", pack: "Arie & Bastiaan Classics" },
  "abc-gelijk": { label: "Arie Heeft Gelijk", icon: "\u2705", url: "/sounds/abc-gelijk.mp3", pack: "Arie & Bastiaan Classics" },
  "abc-moeders": { label: "Moeders", icon: "\u{1F469}", url: "/sounds/abc-moeders.mp3", pack: "Arie & Bastiaan Classics" },
  "abc-vuist": { label: "Gebalde Vuist", icon: "\u270A", url: "/sounds/abc-vuist.mp3", pack: "Arie & Bastiaan Classics" },
  "abc-sjonge": { label: "Sjonge Jonge", icon: "\u{1F629}", url: "/sounds/abc-sjonge.mp3", pack: "Arie & Bastiaan Classics" },
  "abc-aziel": { label: "Azielzoekers", icon: "\u{1F3E0}", url: "/sounds/abc-aziel.mp3", pack: "Arie & Bastiaan Classics" },
  "abc-nietvinden": { label: "Niet Vinden", icon: "\u{1F50D}", url: "/sounds/abc-nietvinden.mp3", pack: "Arie & Bastiaan Classics" },
  "abc-rietje": { label: "Rietje Verdonk", icon: "\u{1F621}", url: "/sounds/abc-rietje.mp3", pack: "Arie & Bastiaan Classics" },
  // ── Zeebats ──
  "zb-lekker": { label: "Lekker!", icon: "\u{1F60B}", url: "/sounds/zb-lekker.mp3", pack: "Zeebats" },
  "zb-wtfhey": { label: "WTF Hey", icon: "\u{1F632}", url: "/sounds/zb-wtfhey.mp3", pack: "Zeebats" },
  "zb-village": { label: "Village People", icon: "\u{1F57A}", url: "/sounds/zb-village.mp3", pack: "Zeebats" },
  "zb-bloemen": { label: "Bloemen Stuk", icon: "\u{1F33B}", url: "/sounds/zb-bloemen.mp3", pack: "Zeebats" },
  "zb-hoegaat": { label: "Hoe Gaat Het", icon: "\u{1F44B}", url: "/sounds/zb-hoegaat.mp3", pack: "Zeebats" },
  "zb-hmmmm": { label: "Hmmmm", icon: "\u{1F914}", url: "/sounds/zb-hmmmm.mp3", pack: "Zeebats" },
  "zb-huuuhuu": { label: "Huuuhuu", icon: "\u{1F62D}", url: "/sounds/zb-huuuhuu.mp3", pack: "Zeebats" },
  "zb-homomex": { label: "Homo Met Ex", icon: "\u{1F494}", url: "/sounds/zb-homomex.mp3", pack: "Zeebats" },
  "zb-nietom": { label: "Niet Om", icon: "\u261D\uFE0F", url: "/sounds/zb-nietom.mp3", pack: "Zeebats" },
  "zb-jeoma": { label: "Je Oma", icon: "\u{1F475}", url: "/sounds/zb-jeoma.mp3", pack: "Zeebats" },
  "zb-pleur": { label: "Bakkie Pleur", icon: "\u2615", url: "/sounds/zb-pleur.mp3", pack: "Zeebats" },
  "zb-koffie": { label: "Bakkie Koffie", icon: "\u2615", url: "/sounds/zb-koffie.mp3", pack: "Zeebats" },
  "zb-sorry": { label: "Sorry Man", icon: "\u{1F64F}", url: "/sounds/zb-sorry.mp3", pack: "Zeebats" },
  "zb-lul": { label: "Je Lul", icon: "\u{1F60F}", url: "/sounds/zb-lul.mp3", pack: "Zeebats" },
  "zb-chinees": { label: "Chinees", icon: "\u{1F372}", url: "/sounds/zb-chinees.mp3", pack: "Zeebats" }
};
var PREMIUM_SOUNDBITE_KEYS = /* @__PURE__ */ new Set([
  "balie",
  "denketniet",
  "vergiftigen",
  "rustaaagh",
  "opjemuil",
  "datzieje"
]);
var SOUNDBITE_PACK_UNLOCK = {
  "Gino Pietermaai": { shopKey: "soundbitepack_gino" },
  "The Champ": { shopKey: "soundbitepack_champ", achievement: { key: "champion", tier: "silver" } },
  "Arie & Bastiaan Vol. 1": { shopKey: "soundbitepack_ab1" },
  "Arie & Bastiaan Vol. 2": { shopKey: "soundbitepack_ab2", achievement: { key: "veteran", tier: "bronze" } },
  "Arie & Bastiaan Vol. 3": { shopKey: "soundbitepack_ab3" },
  "Arie & Bastiaan Classics": { shopKey: "soundbitepack_abc", achievement: { key: "high_roller", tier: "bronze" } },
  "Zeebats": { shopKey: "soundbitepack_zb", achievement: { key: "win_streak", tier: "bronze" } }
};
function isSoundbiteOwned(id, preset, inventory, achievements) {
  const packUnlock = SOUNDBITE_PACK_UNLOCK[preset.pack];
  if (packUnlock) {
    if (packUnlock.shopKey && inventory.includes(packUnlock.shopKey)) return true;
    if (packUnlock.achievement && achievements) {
      const tierOrder = ["bronze", "silver", "gold"];
      const required = tierOrder.indexOf(packUnlock.achievement.tier);
      const earned = achievements.find((a) => a.achievementKey === packUnlock.achievement.key);
      if (earned && tierOrder.indexOf(earned.tier) >= required) return true;
    }
    return false;
  }
  if (PREMIUM_SOUNDBITE_KEYS.has(id)) return inventory.includes(`soundbite_${id}`);
  return true;
}
var SOUNDBITE_COOLDOWN_MS = 1500;

// src/constants/achievements.ts
var ACHIEVEMENT_TIERS = ["bronze", "silver", "gold"];
var ACHIEVEMENT_DEFINITIONS = [
  {
    key: "bust_magnet",
    name: "Bust Magnet",
    description: "Total busts across all games",
    icon: "\u{1F4A5}",
    tiers: {
      bronze: { threshold: 10, title: "Bust Magnet" },
      silver: { threshold: 50, title: "Bust Collector" },
      gold: { threshold: 200, title: "The Unlucky" }
    },
    stat: "totalBusts",
    digitalOnly: false
  },
  {
    key: "lucky_seven",
    name: "Lucky 7 Legend",
    description: "Total Flip 7 bonuses earned",
    icon: "\u2B50",
    tiers: {
      bronze: { threshold: 3, title: "Lucky" },
      silver: { threshold: 15, title: "Seven Chaser" },
      gold: { threshold: 50, title: "Seven Star" }
    },
    stat: "totalFlip7s",
    digitalOnly: true
  },
  {
    key: "untouchable",
    name: "Untouchable",
    description: "Games won without busting once",
    icon: "\u{1F6E1}\uFE0F",
    tiers: {
      bronze: { threshold: 3, title: "Careful" },
      silver: { threshold: 10, title: "Untouchable" },
      gold: { threshold: 25, title: "Invincible" }
    },
    stat: "cleanWins",
    digitalOnly: true
  },
  {
    key: "high_roller",
    name: "High Roller",
    description: "Single-round scores of 30+",
    icon: "\u{1F3B0}",
    tiers: {
      bronze: { threshold: 5, title: "High Roller" },
      silver: { threshold: 20, title: "Big Scorer" },
      gold: { threshold: 50, title: "Jackpot" }
    },
    stat: "highRounds",
    digitalOnly: true
  },
  {
    key: "veteran",
    name: "Veteran",
    description: "Total games completed",
    icon: "\u{1F396}\uFE0F",
    tiers: {
      bronze: { threshold: 10, title: "Rookie" },
      silver: { threshold: 50, title: "Regular" },
      gold: { threshold: 200, title: "OG" }
    },
    stat: "gamesPlayed",
    digitalOnly: false
  },
  {
    key: "champion",
    name: "Champion",
    description: "Total games won",
    icon: "\u{1F3C6}",
    tiers: {
      bronze: { threshold: 5, title: "Winner" },
      silver: { threshold: 25, title: "Contender" },
      gold: { threshold: 100, title: "Grandmaster" }
    },
    stat: "gamesWon",
    digitalOnly: false
  },
  {
    key: "comeback_kid",
    name: "Comeback Kid",
    description: "Won a game after being last after round 1",
    icon: "\u{1F525}",
    tiers: {
      bronze: { threshold: 1, title: "Comeback Kid" },
      silver: { threshold: 5, title: "Phoenix" },
      gold: { threshold: 15, title: "Never Count Me Out" }
    },
    stat: "comebacks",
    digitalOnly: true
  },
  {
    key: "klootzak_enjoyer",
    name: "Klootzak Enjoyer",
    description: "Played the Klootzak soundbite",
    icon: "\u{1F621}",
    tiers: {
      bronze: { threshold: 10 },
      silver: { threshold: 50, title: "Klootzak Fan" },
      gold: { threshold: 200, title: "Certified Klootzak" }
    },
    stat: "klootzakPlays",
    digitalOnly: false
  },
  {
    key: "fietsopa_fan",
    name: "Fietsopa Fan",
    description: "Played the Fietsopa soundbite",
    icon: "\u{1F6B2}",
    tiers: {
      bronze: { threshold: 10 },
      silver: { threshold: 50, title: "Cyclist" },
      gold: { threshold: 200, title: "Tour de Flip" }
    },
    stat: "fietsoplays",
    digitalOnly: false
  },
  {
    key: "social_butterfly",
    name: "Social Butterfly",
    description: "Total reactions sent",
    icon: "\u{1F98B}",
    tiers: {
      bronze: { threshold: 25, title: "Reactor" },
      silver: { threshold: 100, title: "Social Butterfly" },
      gold: { threshold: 500, title: "Life of the Party" }
    },
    stat: "reactionsSent",
    digitalOnly: false
  },
  {
    key: "survivor",
    name: "Survivor",
    description: "Survived with 6 cards (active, not busted)",
    icon: "\u{1F340}",
    tiers: {
      bronze: { threshold: 3, title: "Survivor" },
      silver: { threshold: 15, title: "Daredevil" },
      gold: { threshold: 50, title: "Edge Walker" }
    },
    stat: "sixCardSurvivals",
    digitalOnly: true
  },
  {
    key: "bust_streak",
    name: "Kamikaze",
    description: "Busted 3+ rounds in a row in a single game",
    icon: "\u{1F4A3}",
    tiers: {
      bronze: { threshold: 1, title: "Kamikaze" },
      silver: { threshold: 3, title: "Glutton for Punishment" },
      gold: { threshold: 10, title: "Reckless" }
    },
    stat: "bustStreaks",
    digitalOnly: false
  },
  {
    key: "perfect_game",
    name: "Perfectionist",
    description: "Won a game with the highest round score every round",
    icon: "\u{1F48E}",
    tiers: {
      bronze: { threshold: 1, title: "Perfectionist" },
      silver: { threshold: 3, title: "Dominant" },
      gold: { threshold: 10, title: "Flawless" }
    },
    stat: "perfectGames",
    digitalOnly: true
  },
  {
    key: "coin_collector",
    name: "Coin Collector",
    description: "Total coins earned across all games",
    icon: "\u{1FA99}",
    tiers: {
      bronze: { threshold: 500, title: "Penny Pincher" },
      silver: { threshold: 2500, title: "Coin Collector" },
      gold: { threshold: 1e4, title: "Moneybags" }
    },
    stat: "totalCoinsEarned",
    digitalOnly: true
  },
  {
    key: "win_streak",
    name: "Win Streak",
    description: "Won consecutive games in a row",
    icon: "\u{1F525}",
    tiers: {
      bronze: { threshold: 2, title: "Hot Streak" },
      silver: { threshold: 4, title: "On Fire" },
      gold: { threshold: 7, title: "Unstoppable" }
    },
    stat: "longestWinStreak",
    digitalOnly: true
  },
  {
    key: "vengeance_master",
    name: "Vengeance Master",
    description: "Games won in Vengeance or Brutal mode",
    icon: "\u2694\uFE0F",
    tiers: {
      bronze: { threshold: 3, title: "Avenger" },
      silver: { threshold: 10, title: "Vengeance Master" },
      gold: { threshold: 30, title: "Warlord" }
    },
    stat: "vengeanceWins",
    digitalOnly: true
  },
  {
    key: "customizer",
    name: "Fashionista",
    description: "Items purchased from the shop",
    icon: "\u{1F6CD}\uFE0F",
    tiers: {
      bronze: { threshold: 3, title: "Shopper" },
      silver: { threshold: 8, title: "Fashionista" },
      gold: { threshold: 15, title: "Style Icon" }
    },
    stat: "itemsPurchased",
    digitalOnly: false
  },
  {
    key: "daily_player",
    name: "Daily Devotee",
    description: "Daily challenges completed",
    icon: "\u{1F4C5}",
    tiers: {
      bronze: { threshold: 5, title: "Daily Player" },
      silver: { threshold: 20, title: "Devoted" },
      gold: { threshold: 50, title: "Daily Devotee" }
    },
    stat: "dailyChallengesCompleted",
    digitalOnly: true
  }
];
var ACHIEVEMENT_MAP = Object.fromEntries(
  ACHIEVEMENT_DEFINITIONS.map((a) => [a.key, a])
);

// src/constants/shop.ts
var COIN_REWARDS = {
  GAME_COMPLETE: 10,
  GAME_WIN: 25,
  ACHIEVEMENT_BRONZE: 15,
  ACHIEVEMENT_SILVER: 30,
  ACHIEVEMENT_GOLD: 50
};
var SHOP_ITEMS = [
  // Premium titles (not earnable through achievements)
  {
    key: "title_emperor",
    name: "Emperor",
    description: "A title fit for royalty",
    type: "title",
    price: 200,
    icon: "\u{1F451}",
    titleText: "Emperor"
  },
  {
    key: "title_legend",
    name: "The Legend",
    description: "Legendary status, purchased",
    type: "title",
    price: 300,
    icon: "\u2728",
    titleText: "The Legend"
  },
  {
    key: "title_kaaskop",
    name: "Kaaskop",
    description: "Dutch and proud",
    type: "title",
    price: 100,
    icon: "\u{1F9C0}",
    titleText: "Kaaskop"
  },
  {
    key: "title_cardshark",
    name: "Card Shark",
    description: "Fear the shark",
    type: "title",
    price: 150,
    icon: "\u{1F988}",
    titleText: "Card Shark"
  },
  {
    key: "title_goblin",
    name: "Goblin Mode",
    description: "Unhinged and loving it",
    type: "title",
    price: 100,
    icon: "\u{1F47A}",
    titleText: "Goblin Mode"
  },
  // Themes
  {
    key: "theme_dark",
    name: "Dark Table",
    description: "Sleek dark mode for late-night games",
    type: "theme",
    price: 150,
    icon: "\u{1F319}"
  },
  {
    key: "theme_ocean",
    name: "Ocean Breeze",
    description: "Cool blue tones",
    type: "theme",
    price: 150,
    icon: "\u{1F30A}"
  },
  {
    key: "theme_forest",
    name: "Forest Floor",
    description: "Deep earthy greens",
    type: "theme",
    price: 150,
    icon: "\u{1F332}"
  },
  {
    key: "theme_royal",
    name: "Royal Purple",
    description: "Regal purple and gold",
    type: "theme",
    price: 200,
    icon: "\u{1F49C}"
  },
  // Soundpacks
  {
    key: "soundpack_retro",
    name: "Retro Arcade",
    description: "8-bit sound effects for card draws and busts",
    type: "soundpack",
    price: 200,
    icon: "\u{1F579}\uFE0F"
  },
  {
    key: "soundpack_dramatic",
    name: "Dramatic",
    description: "Over-the-top dramatic sounds for every moment",
    type: "soundpack",
    price: 200,
    icon: "\u{1F3AD}"
  },
  {
    key: "soundpack_chill",
    name: "Lo-Fi Chill",
    description: "Relaxed vibes for a chill game night",
    type: "soundpack",
    price: 200,
    icon: "\u{1F3B6}"
  },
  // Premium soundbites
  {
    key: "soundbite_balie",
    name: "Balie",
    description: "Jij trekt toch iedereen over de balie",
    type: "soundbite",
    price: 75,
    icon: "\u{1F44A}",
    soundbiteKey: "balie"
  },
  {
    key: "soundbite_denketniet",
    name: "Denk t Niet",
    description: "Nee, denk het niet",
    type: "soundbite",
    price: 75,
    icon: "\u{1F645}",
    soundbiteKey: "denketniet"
  },
  {
    key: "soundbite_vergiftigen",
    name: "Vergiftigen",
    description: "Wil je mij vergiftigen?!",
    type: "soundbite",
    price: 100,
    icon: "\u{1F4A7}",
    soundbiteKey: "vergiftigen"
  },
  {
    key: "soundbite_rustaaagh",
    name: "Rustaaagh",
    description: "RUSTAAAGH!",
    type: "soundbite",
    price: 75,
    icon: "\u{1F92C}",
    soundbiteKey: "rustaaagh"
  },
  {
    key: "soundbite_opjemuil",
    name: "Op Je Muil",
    description: "5 euros... op je muil gauw",
    type: "soundbite",
    price: 100,
    icon: "\u{1F4B5}",
    soundbiteKey: "opjemuil"
  },
  {
    key: "soundbite_datzieje",
    name: "Dat Zie Je",
    description: "Dat zie je toch gvd toch",
    type: "soundbite",
    price: 75,
    icon: "\u{1F624}",
    soundbiteKey: "datzieje"
  },
  // Soundbite packs
  {
    key: "soundbitepack_gino",
    name: "Gino Pietermaai Pack",
    description: "17 soundbites from Gino Pietermaai",
    type: "soundbitepack",
    price: 500,
    icon: "\u{1F3AC}"
  },
  {
    key: "soundbitepack_champ",
    name: "The Champ Pack",
    description: "6 soundbites from The Champ \u2014 also unlocked at Champion Silver",
    type: "soundbitepack",
    price: 2e3,
    icon: "\u{1F3C6}"
  },
  {
    key: "soundbitepack_ab1",
    name: "Arie & Bastiaan Vol. 1",
    description: "8 classic soundbites from Arie & Bastiaan",
    type: "soundbitepack",
    price: 200,
    icon: "\u{1F921}"
  },
  {
    key: "soundbitepack_ab2",
    name: "Arie & Bastiaan Vol. 2",
    description: "8 soundbites \u2014 also unlocked at Veteran Bronze",
    type: "soundbitepack",
    price: 300,
    icon: "\u{1F921}"
  },
  {
    key: "soundbitepack_ab3",
    name: "Arie & Bastiaan Vol. 3",
    description: "8 more Arie & Bastiaan soundbites",
    type: "soundbitepack",
    price: 500,
    icon: "\u{1F921}"
  },
  {
    key: "soundbitepack_abc",
    name: "Arie & Bastiaan Classics",
    description: "15 legendary soundbites \u2014 also unlocked at High Roller Bronze",
    type: "soundbitepack",
    price: 750,
    icon: "\u{1F3C6}"
  },
  {
    key: "soundbitepack_zb",
    name: "Zeebats Pack",
    description: "15 Zeebats soundbites \u2014 also unlocked at Win Streak Bronze",
    type: "soundbitepack",
    price: 1e3,
    icon: "\u{1F30A}"
  },
  // Premium avatars
  {
    key: "avatar_crown",
    name: "Crown",
    description: "The king of the table",
    type: "avatar",
    price: 100,
    icon: "\u{1F451}"
  },
  {
    key: "avatar_dragon",
    name: "Dragon",
    description: "Fear the dragon",
    type: "avatar",
    price: 150,
    icon: "\u{1F409}"
  },
  {
    key: "avatar_unicorn",
    name: "Unicorn",
    description: "Magical and majestic",
    type: "avatar",
    price: 150,
    icon: "\u{1F984}"
  },
  {
    key: "avatar_alien",
    name: "Alien",
    description: "Out of this world",
    type: "avatar",
    price: 100,
    icon: "\u{1F47D}"
  },
  {
    key: "avatar_devil",
    name: "Devil",
    description: "Devilishly good at cards",
    type: "avatar",
    price: 200,
    icon: "\u{1F608}"
  }
];
var SHOP_ITEM_MAP = Object.fromEntries(
  SHOP_ITEMS.map((item) => [item.key, item])
);
var THEME_DEFINITIONS = [
  {
    key: "theme_dark",
    name: "Dark Table",
    vars: {
      "--cream": "#1A1A2E",
      "--cream-dark": "#16162A",
      "--parchment": "#222240",
      "--maroon": "#E94560",
      "--maroon-deep": "#C73550",
      "--gold": "#F0C040",
      "--gold-light": "#FFD866",
      "--gold-dim": "#C9A030",
      "--gold-pale": "rgba(240, 192, 64, 0.15)",
      "--text-dark": "#E8E8F0",
      "--text-light": "#1A1A2E",
      "--text-muted": "#8888AA",
      "--text-faint": "#555577",
      "--border-ornate": "#444466",
      "--border-subtle": "#333355",
      "--border-gold": "#F0C040",
      "--shadow": "rgba(0, 0, 0, 0.4)",
      "--shadow-heavy": "rgba(0, 0, 0, 0.6)",
      "--color-bust-bg": "rgba(233, 69, 96, 0.15)",
      "--color-stay-bg": "rgba(46, 125, 50, 0.15)",
      "--color-freeze-bg": "rgba(2, 136, 209, 0.15)"
    }
  },
  {
    key: "theme_ocean",
    name: "Ocean Breeze",
    vars: {
      "--cream": "#E8F0F8",
      "--cream-dark": "#D0E0F0",
      "--parchment": "#F0F6FC",
      "--maroon": "#1565C0",
      "--maroon-deep": "#0D47A1",
      "--gold": "#FF8F00",
      "--gold-light": "#FFA726",
      "--gold-dim": "#E07000",
      "--gold-pale": "rgba(255, 143, 0, 0.1)",
      "--text-dark": "#1A2A3A",
      "--text-light": "#F0F6FC",
      "--text-muted": "#5A7A9A",
      "--text-faint": "#8AA0B8",
      "--border-ornate": "#90B0D0",
      "--border-subtle": "#B8D0E4",
      "--border-gold": "#FF8F00",
      "--shadow": "rgba(10, 30, 60, 0.15)",
      "--shadow-heavy": "rgba(10, 30, 60, 0.35)"
    }
  },
  {
    key: "theme_forest",
    name: "Forest Floor",
    vars: {
      "--cream": "#E8EDE4",
      "--cream-dark": "#D4DCC8",
      "--parchment": "#F0F4EC",
      "--maroon": "#2E7D32",
      "--maroon-deep": "#1B5E20",
      "--gold": "#D4A017",
      "--gold-light": "#E6B422",
      "--gold-dim": "#B38A10",
      "--gold-pale": "rgba(212, 160, 23, 0.1)",
      "--text-dark": "#1C2C18",
      "--text-light": "#F0F4EC",
      "--text-muted": "#6A8060",
      "--text-faint": "#96AC88",
      "--border-ornate": "#8AA878",
      "--border-subtle": "#B4C8A8",
      "--border-gold": "#D4A017",
      "--shadow": "rgba(20, 40, 15, 0.15)",
      "--shadow-heavy": "rgba(20, 40, 15, 0.35)"
    }
  },
  {
    key: "theme_royal",
    name: "Royal Purple",
    vars: {
      "--cream": "#F0E8F5",
      "--cream-dark": "#DDD0E8",
      "--parchment": "#F6F0FA",
      "--maroon": "#6A1B9A",
      "--maroon-deep": "#4A148C",
      "--gold": "#FFD700",
      "--gold-light": "#FFE44D",
      "--gold-dim": "#C9A800",
      "--gold-pale": "rgba(255, 215, 0, 0.1)",
      "--text-dark": "#2A1838",
      "--text-light": "#F6F0FA",
      "--text-muted": "#7A6890",
      "--text-faint": "#A898B8",
      "--border-ornate": "#B090C8",
      "--border-subtle": "#D0C0E0",
      "--border-gold": "#FFD700",
      "--shadow": "rgba(40, 15, 60, 0.15)",
      "--shadow-heavy": "rgba(40, 15, 60, 0.35)"
    }
  }
];
var THEME_MAP = Object.fromEntries(
  THEME_DEFINITIONS.map((t) => [t.key, t])
);
var FREE_AVATARS = [
  "\u{1F3B2}",
  // dice
  "\u{1F0A0}",
  // joker card
  "\u{1F916}",
  // robot
  "\u{1F47B}",
  // ghost
  "\u{1F63A}",
  // cat
  "\u{1F436}",
  // dog
  "\u{1F989}",
  // owl
  "\u{1F680}",
  // rocket
  "\u2B50",
  // star
  "\u{1F31F}",
  // glowing star
  "\u{1F525}",
  // fire
  "\u2744\uFE0F"
  // snowflake
];

// src/constants/shiny.ts
var SHINY_TIERS = ["holo", "reverse-holo", "full-art", "alt-art", "gold-secret"];
var SHINY_TIER_ORDER = {
  "holo": 1,
  "reverse-holo": 2,
  "full-art": 3,
  "alt-art": 4,
  "gold-secret": 5
};
function highestTier(tiers) {
  if (tiers.length === 0) return null;
  return tiers.reduce((a, b) => SHINY_TIER_ORDER[a] >= SHINY_TIER_ORDER[b] ? a : b);
}
function rollShinyTier(card) {
  const canAltArt = isActionCard(card) || card.type === "number" && "special" in card && !!card.special;
  const weights = [
    ["holo", 50],
    ["reverse-holo", 30],
    ["full-art", 15],
    ...canAltArt ? [["alt-art", 4]] : [],
    ["gold-secret", 1]
  ];
  const total = weights.reduce((sum, [, w]) => sum + w, 0);
  let roll = Math.random() * total;
  for (const [tier, weight] of weights) {
    roll -= weight;
    if (roll <= 0) return tier;
  }
  return "holo";
}
var SHINY_CHANCE = 1 / 2e3;
var SHINY_EVENT = {
  name: "Shiny Season",
  multiplier: 10,
  endsAt: /* @__PURE__ */ new Date("2026-04-06T23:59:59Z")
};
function getShinyChance() {
  if (SHINY_EVENT && /* @__PURE__ */ new Date() < SHINY_EVENT.endsAt) {
    return SHINY_CHANCE * SHINY_EVENT.multiplier;
  }
  return SHINY_CHANCE;
}
var SHINY_ITEM_PREFIX = "shiny:";
function shinyItemKey(tier, cardKey) {
  return `${SHINY_ITEM_PREFIX}${tier}:${cardKey}`;
}
function parseShinyItemKey(itemKey) {
  if (!itemKey.startsWith(SHINY_ITEM_PREFIX)) return null;
  const rest = itemKey.slice(SHINY_ITEM_PREFIX.length);
  for (const tier of SHINY_TIERS) {
    const prefix = tier + ":";
    if (rest.startsWith(prefix)) {
      return { tier, cardKey: rest.slice(prefix.length) };
    }
  }
  return { tier: "full-art", cardKey: rest };
}
function getCardKey(card) {
  if (card.type === "number" && "special" in card && card.special) {
    return `number:${card.special}`;
  }
  return `${card.type}:${card.value}`;
}
function cardKeyToCard(key) {
  const colonIdx = key.indexOf(":");
  const type = key.slice(0, colonIdx);
  const value = key.slice(colonIdx + 1);
  if (type === "number") {
    if (value === "unlucky7") {
      return { type: "number", value: 7, special: "unlucky7" };
    }
    if (value === "lucky13") {
      return { type: "number", value: 13, special: "lucky13" };
    }
    return { type: "number", value: parseInt(value, 10) };
  }
  if (type === "modifier") {
    return { type: "modifier", value };
  }
  return { type: "action", value };
}
var ALL_STANDARD_CARD_KEYS = [
  ...NUMBER_CARD_VALUES.map((v) => `number:${v}`),
  ...MODIFIER_CARD_VALUES.map((v) => `modifier:${v}`),
  ...ACTION_CARD_VALUES.map((v) => `action:${v}`)
];
var ALL_VENGEANCE_CARD_KEYS = [
  "number:13",
  "number:unlucky7",
  "number:lucky13",
  ...VENGEANCE_MODIFIER_CARD_VALUES.map((v) => `modifier:${v}`),
  ...VENGEANCE_ACTION_CARD_VALUES.map((v) => `action:${v}`)
];
var ALL_CARD_KEYS = [
  ...ALL_STANDARD_CARD_KEYS,
  ...ALL_VENGEANCE_CARD_KEYS
];

// src/constants/hot-streak.ts
var HS_CARD_FLIP_INTERVAL_MS = 1500;
var HS_BETTING_TIMER_MS = 15e3;
var HS_CARD_SUBMIT_TIMER_MS = 3e4;
var HS_DOUBLE_DOWN_TIMER_MS = 15e3;
var HS_RACE_COUNTDOWN_MS = 3e3;
var HS_RACE_RESULTS_DISPLAY_MS = 1e4;
var HS_FOLD_ANIMATION_MS = 2e3;
var HS_BURN_CARD_COUNT = 3;
var HS_TOTAL_RACE_DECK_SIZE = 18;
var HS_TOTAL_RACES = 3;
var HS_STARTING_MONEY = 10;
var HS_MIN_PLAYERS = 1;
var HS_MAX_PLAYERS = 8;
var HS_TRACK_CONFIG = {
  totalSpaces: 20,
  foldPosition: 0,
  // Starts at 0, increases on each fold
  starPositions: [5, 10, 15],
  foldLines: [4, 8, 12],
  // Track folds at these positions from the back
  finishLine: 20
};
var HS_LANES = 4;
var HS_MASCOT_IDS = ["hurley", "gobbler", "dangle", "mum"];
var HS_MASCOTS = {
  hurley: {
    id: "hurley",
    name: "Hurley",
    fullName: "Hurley the Bun Banger",
    color: "#e8594a",
    lore: "Don't be fooled \u2014 Hurley is no hot dog. Boxford, a humble pork processing town, independently invented their own unrelated meat-filled tube on a soft split bun back in the 1880s, calling it a bun banger."
  },
  gobbler: {
    id: "gobbler",
    name: "Gobbler",
    fullName: "Gobbler the Turkey-Bear",
    color: "#e87d2e",
    lore: "Until 1935, Glenbrook's mascot was Tony the Turkey, a beloved town pet who wandered freely, presided over ribbon cuttings, and enjoyed annual Thanksgiving immunity."
  },
  dangle: {
    id: "dangle",
    name: "Dangle",
    fullName: "Dangle the Deep Sea Fish",
    color: "#3b6fb5",
    lore: "Dangle is a mascot birthed in disaster. In 1905, the perpetual underdogs, the Ashford Angels, had clawed their way to the playoffs when a freak waterspout dropped hundreds of deep sea creatures onto the field."
  },
  mum: {
    id: "mum",
    name: "Mum",
    fullName: "Mum the Queen",
    color: "#c94b8a",
    lore: "Thanks to a quirk of mapmaking and a catastrophic postal failure, Queveland never actually left British rule. Today, Queveland proudly flies the Union Jack, serves mince pies and earl grey at ballgames."
  }
};
var HS_RANDOM_CARDS_BY_PLAYER_COUNT = {
  1: 13,
  // 1-player: 4 starting + 13 random + 1 submitted = 18
  2: 10,
  // 2-player: 4 starting + 10 random + 4 submitted = 18
  3: 11,
  // 3-player: 4 starting + 11 random + 3 submitted = 18
  4: 10,
  // 4-player: 4 starting + 10 random + 4 submitted = 18
  5: 9,
  6: 8,
  7: 7,
  8: 6
};
var HS_HAND_SIZE_BY_PLAYER_COUNT = {
  1: 3,
  2: 4,
  3: 3,
  4: 3,
  5: 3,
  6: 3,
  7: 3,
  8: 3
};
var HS_CARDS_SUBMITTED_BY_PLAYER_COUNT = {
  1: 1,
  2: 2,
  3: 1,
  4: 1,
  5: 1,
  6: 1,
  7: 1,
  8: 1
};
var HS_TICKETS_PER_PLAYER_BY_PLAYER_COUNT = {
  1: 2,
  2: 3,
  3: 2,
  4: 2,
  5: 2,
  6: 2,
  7: 2,
  8: 2
};
var HS_MASCOT_TICKET_PAYOUTS = {
  1: { safe: { first: 10, second: 7, third: 5 }, risky: { first: 15, second: 5, third: 2 } },
  2: { safe: { first: 7, second: 5, third: 3 }, risky: { first: 11, second: 3, third: 1 } },
  3: { safe: { first: 5, second: 3, third: 2 }, risky: { first: 7, second: 2, third: 0 } }
};
var HS_SIDE_BET_PAYOUT = {
  safe: { correct: 10, incorrect: 0 },
  risky: { correct: 15, incorrect: -5 }
};
var HS_SIDE_BET_QUESTIONS = [
  "Will at least 1 mascot be disqualified?",
  "Will Hurley finish in the top 2?",
  "Will Gobbler finish in the top 2?",
  "Will Dangle finish in the top 2?",
  "Will Mum finish in the top 2?",
  "Will any mascot turn around?",
  "Will any mascot fall down at least twice?",
  "Will the track fold at least twice?",
  "Will 2 or more mascots be disqualified?",
  "Will any mascot collide with another?",
  "Will the last-place finisher be DQ'd?",
  "Will all 4 mascots still be standing at the end?"
];
var HS_SIDE_BETS_PER_RACE = 2;
function mascotCard(mascot, type, value) {
  return { type, mascot, value };
}
function multiCard(effect, value) {
  return { type: "multi", mascot: "all", multiEffect: { type: effect, value } };
}
var HS_CARD_TEMPLATES = [
  // Per mascot: +1, +2, +3, -1, -2, star, turn-around, fall-down, swerve-left, swerve-right, recover
  ...HS_MASCOT_IDS.flatMap((m) => [
    mascotCard(m, "number", 1),
    mascotCard(m, "number", 2),
    mascotCard(m, "number", 3),
    mascotCard(m, "number", -1),
    mascotCard(m, "number", -2),
    mascotCard(m, "star"),
    mascotCard(m, "turn-around"),
    mascotCard(m, "fall-down"),
    mascotCard(m, "swerve-left", 1),
    mascotCard(m, "swerve-right", 1),
    mascotCard(m, "recover", 1)
  ]),
  // Green multi cards (5 cards total)
  multiCard("number", 2),
  multiCard("number", 1),
  multiCard("number", -1),
  multiCard("recover", 1),
  multiCard("star"),
  // Additional mascot cards for variety (to reach ~53)
  mascotCard("hurley", "number", 1),
  mascotCard("gobbler", "number", 2),
  mascotCard("dangle", "number", 1),
  mascotCard("mum", "number", 2),
  mascotCard("hurley", "swerve-left", 2),
  mascotCard("gobbler", "swerve-right", 2),
  mascotCard("dangle", "fall-down"),
  mascotCard("mum", "turn-around")
];
var HS_STARTING_CARD_TEMPLATES = HS_MASCOT_IDS.map(
  (m) => mascotCard(m, "number", 1)
);
var HS_LIFE_OUTCOMES = {
  0: "You have not won enough money. You become feral.",
  1: "Ape got ya.",
  2: "You shred your losing tickets to build a cozy nest for your geckos.",
  3: "Your winnings don't add up to much but you fold them over and use them to fix a wiggly table. Nice!",
  4: "Your winnings cover the cost of a coffee. You spill it on a rat who's trying to slurp your shoelace like spaghetti.",
  5: "You treat yourself to a nice stadium dinner of free diced onions and a bottled water.",
  6: 'You slip your winnings under the windshield wiper of a random car in the stadium lot to "pay it forward" in a confusing way.',
  7: "You buy one share of stock in Mascot Racing League Worldwide Corp. Buy what you know!",
  8: "You've got just enough for a cheap net to capture the mayor's poodle for ransom, and a dollar left over as poodle bait.",
  9: "You sell your winnings on Facebook Marketplace for $8.",
  10: "You leave with the exact same amount of money you started with, go to bed, and wake up at the start of the day you just lived. Here we go again!!",
  11: "Thanks to your gambling, you can buy your family some ketchup.",
  12: "You buy a cursed ring. But it's not a big deal curse. It's fine.",
  13: "You sneeze all over your winnings, so now they're gross, so into the trash they go.",
  14: "You're disgusted at yourself for gambling. You go to church and have them hose you down with holy water.",
  15: "At $15, you buy the stadium, you buy your son a Hurley action figure with juice oozing action. The little guy's just crazy about meat juices!",
  16: "You use your winnings on a Frankenstein mask to kickstart your door-to-door Frankenstein business.",
  17: "You ask for your winnings in coins. They weigh you down and you're sucked into a mud puddle forever.",
  18: "Your winnings give you a paper cut so you flush them as revenge.",
  19: "Your winnings make you bold enough to quit your job. Then you count them again. Oops.",
  20: "You're overcome with the irrepressible urge to eat a $20 bill and devour your winnings. The rest of your life is normal.",
  25: "You buy a hardcover book about what to do with $25. It says you made the best possible choice. Wow!",
  30: "Ape tried to get ya, but you paid it off with a cool $30.",
  35: "You buy yourself a new boomerang. Someday you will learn to throw them right.",
  40: "You use your winnings to buy a copy of the game Hot Streak, by Jon Perry and CMYK. You enjoy playing it for years with friends and family.",
  45: "You have enough to buy yourself a single mascot glove. Soon, you will be the mascot.",
  50: "Since $50 is objectively the coolest amount of money, you become wildly popular, with many girlfriends and/or boyfriends."
};
function getLifeOutcome(money) {
  if (money >= 100) return "You have won too much money. You become feral.";
  const keys = Object.keys(HS_LIFE_OUTCOMES).map(Number).sort((a, b) => b - a);
  for (const key of keys) {
    if (money >= key) return HS_LIFE_OUTCOMES[key];
  }
  return HS_LIFE_OUTCOMES[0];
}
var ticketIdCounter = 0;
function buildBettingTickets(sideQuestions) {
  const tickets = [];
  for (const mascot of HS_MASCOT_IDS) {
    for (const tier of [1, 2, 3]) {
      const payouts = HS_MASCOT_TICKET_PAYOUTS[tier];
      tickets.push({
        id: `mascot-${mascot}-${tier}-${ticketIdCounter++}`,
        type: "mascot",
        mascot,
        tier,
        safePayout: payouts.safe,
        riskyPayout: payouts.risky
      });
    }
  }
  for (const question of sideQuestions) {
    tickets.push({
      id: `side-${ticketIdCounter++}`,
      type: "side",
      question,
      tier: 1,
      safePayout: HS_SIDE_BET_PAYOUT.safe,
      riskyPayout: HS_SIDE_BET_PAYOUT.risky
    });
  }
  return tickets;
}
function pickSideBetQuestions(count) {
  const shuffled = [...HS_SIDE_BET_QUESTIONS].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

// src/constants/duel-cards.ts
var DUEL_CARD_DEFINITIONS = {
  peek: {
    type: "peek",
    category: "action",
    name: "Peek",
    icon: "\u{1F441}\uFE0F",
    description: "See the next card before deciding hit/stay"
  },
  shield: {
    type: "shield",
    category: "action",
    name: "Shield",
    icon: "\u{1F6E1}\uFE0F",
    description: "Survive one bust this round"
  },
  forceHit: {
    type: "forceHit",
    category: "action",
    name: "Force Hit",
    icon: "\u{1F4A5}",
    description: "Opponent must hit once instead of staying"
  },
  doubleScore: {
    type: "doubleScore",
    category: "action",
    name: "Double Score",
    icon: "\u2B50",
    description: "+50% round score at end of round"
  },
  headStart: {
    type: "headStart",
    category: "perk",
    name: "Head Start",
    icon: "\u{1F3C3}",
    description: "Start each round with +3 points"
  },
  wideNet: {
    type: "wideNet",
    category: "perk",
    name: "Wide Net",
    icon: "\u{1F310}",
    description: "Bust at 8 unique numbers instead of 7"
  }
};
var DUEL_ACTION_CARDS = ["peek", "shield", "forceHit", "doubleScore"];
var DUEL_PERK_CARDS = ["headStart", "wideNet"];
var DUEL_MAX_ACTIONS = 2;
var DUEL_MAX_PERKS = 1;
var DUEL_TARGET_SCORE = 50;
var DUEL_BEST_OF = 3;
var STARTER_PACK = [
  "peek",
  "peek",
  "shield",
  "shield",
  "forceHit",
  "forceHit",
  "doubleScore",
  "doubleScore",
  "headStart",
  "wideNet",
  "peek"
  // bonus random replaced at claim time
];
var DUEL_PACKS = {
  common: {
    name: "Common Pack",
    price: 100,
    cardCount: 3
  }
};

// src/deck/deck.ts
function buildDeck(variant = "standard") {
  const deck = [];
  for (const [value, count] of Object.entries(NUMBER_CARD_COUNTS)) {
    for (let i = 0; i < count; i++) {
      deck.push({ type: "number", value: Number(value) });
    }
  }
  for (const [value, count] of Object.entries(MODIFIER_CARD_COUNTS)) {
    for (let i = 0; i < count; i++) {
      deck.push({ type: "modifier", value });
    }
  }
  for (const [value, count] of Object.entries(ACTION_CARD_COUNTS)) {
    if (count === 0) continue;
    for (let i = 0; i < count; i++) {
      deck.push({ type: "action", value });
    }
  }
  if (variant === "vengeance" || variant === "vengeance-brutal") {
    for (const { value, special, count } of VENGEANCE_SPECIAL_NUMBER_CARDS) {
      for (let i = 0; i < count; i++) {
        deck.push({ type: "number", value, special });
      }
    }
    for (const [value, count] of Object.entries(VENGEANCE_ACTION_CARD_COUNTS)) {
      for (let i = 0; i < (count ?? 0); i++) {
        deck.push({ type: "action", value });
      }
    }
    for (const [value, count] of Object.entries(VENGEANCE_MODIFIER_CARD_COUNTS)) {
      for (let i = 0; i < (count ?? 0); i++) {
        deck.push({ type: "modifier", value });
      }
    }
  }
  return deck;
}
function shuffleDeck(deck) {
  const shuffled = [...deck];
  const randomValues = typeof globalThis.crypto?.getRandomValues === "function" ? new Uint32Array(shuffled.length) : null;
  if (randomValues) globalThis.crypto.getRandomValues(randomValues);
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = randomValues ? randomValues[i] % (i + 1) : Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
function seededShuffle(deck, seed) {
  const shuffled = [...deck];
  let state = seed | 0;
  function mulberry32() {
    state = state + 1831565813 | 0;
    let t = Math.imul(state ^ state >>> 15, 1 | state);
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  }
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(mulberry32() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
function drawCard(deck) {
  if (deck.length === 0) {
    throw new Error("Deck is empty");
  }
  const [card, ...remaining] = deck;
  return { card, remaining };
}

// src/scoring/calculate-score.ts
function findDuplicateNumber(cards) {
  const numberCards = cards.filter(isNumberCard);
  const counts = /* @__PURE__ */ new Map();
  for (const card of numberCards) {
    const c = (counts.get(card.value) ?? 0) + 1;
    counts.set(card.value, c);
    if (card.value === 13) {
      const hasLucky13 = numberCards.some((nc) => nc.value === 13 && nc.special === "lucky13");
      if (hasLucky13 && c <= 2) continue;
    }
    if (c >= 2) return card.value;
  }
  return null;
}
function hasSecondChance(cards) {
  return cards.some((c) => c.type === "action" && c.value === "second-chance");
}
function countUniqueNumbers(cards) {
  const numberCards = cards.filter(isNumberCard);
  return new Set(numberCards.map((c) => c.value)).size;
}
function calculateScore(cards, isBusted, variant = "standard") {
  if (isBusted) {
    return {
      baseSum: 0,
      hasMultiplier: false,
      hasDiv2: false,
      multipliedBase: 0,
      positiveModifierSum: 0,
      negativeModifierSum: 0,
      flip7Bonus: 0,
      totalScore: 0,
      isBusted: true,
      uniqueNumberCount: countUniqueNumbers(cards)
    };
  }
  const numberCards = cards.filter(isNumberCard);
  const modifierCards = cards.filter(isModifierCard);
  const baseSum = numberCards.reduce((sum, card) => sum + card.value, 0);
  const hasDiv2 = modifierCards.some((c) => c.value === "div2");
  const afterDiv2 = hasDiv2 ? Math.floor(baseSum / 2) : baseSum;
  const hasMultiplier = modifierCards.some((c) => c.value === "x2");
  const multipliedBase = hasMultiplier ? afterDiv2 * 2 : afterDiv2;
  const positiveModifierSum = modifierCards.filter((c) => c.value.startsWith("+")).reduce((sum, card) => {
    return sum + parseInt(card.value.replace("+", ""), 10);
  }, 0);
  const negativeModifierSum = modifierCards.filter((c) => c.value.startsWith("-")).reduce((sum, card) => {
    return sum + parseInt(card.value, 10);
  }, 0);
  const uniqueNumberCount = countUniqueNumbers(cards);
  const flip7Bonus = uniqueNumberCount >= FLIP_7_THRESHOLD ? FLIP_7_BONUS : 0;
  let totalScore = multipliedBase + positiveModifierSum + negativeModifierSum + flip7Bonus;
  if (variant !== "vengeance-brutal") {
    totalScore = Math.max(0, totalScore);
  }
  return {
    baseSum,
    hasMultiplier,
    hasDiv2,
    multipliedBase,
    positiveModifierSum,
    negativeModifierSum,
    flip7Bonus,
    totalScore,
    isBusted: false,
    uniqueNumberCount
  };
}

// src/constants/story.ts
var STORY_CHAPTERS = [
  {
    chapter: 1,
    name: "The Basics",
    description: "Learn the ropes against easy opponents.",
    levels: [
      {
        chapter: 1,
        level: 1,
        botCount: 1,
        botDifficulty: "easy",
        targetScore: 100,
        variant: "standard",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "score-total", value: 100, description: "Finish with 100+ points" },
          { type: "flip7", description: "Achieve a Flip 7" }
        ]
      },
      {
        chapter: 1,
        level: 2,
        botCount: 2,
        botDifficulty: "easy",
        targetScore: 100,
        variant: "standard",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "score-round", value: 30, description: "Score 30+ in one round" },
          { type: "no-bust", description: "Win without busting" }
        ]
      },
      {
        chapter: 1,
        level: 3,
        botCount: 1,
        botDifficulty: "medium",
        targetScore: 100,
        variant: "standard",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "score-total", value: 120, description: "Finish with 120+ points" },
          { type: "flip7", description: "Achieve a Flip 7" }
        ]
      },
      {
        chapter: 1,
        level: 4,
        botCount: 2,
        botDifficulty: "medium",
        targetScore: 150,
        variant: "standard",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "no-bust", description: "Win without busting" },
          { type: "win-streak", value: 2, description: "Win 2 rounds in a row" }
        ]
      },
      {
        chapter: 1,
        level: 5,
        botCount: 1,
        botDifficulty: "medium",
        targetScore: 100,
        variant: "standard",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "score-round", value: 50, description: "Score 50+ in one round" },
          { type: "flip7", description: "Achieve a Flip 7" }
        ]
      },
      {
        chapter: 1,
        level: 6,
        botCount: 1,
        botDifficulty: "hard",
        targetScore: 150,
        variant: "standard",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "score-total", value: 160, description: "Finish with 160+ points" },
          { type: "no-bust", description: "Win without busting" }
        ]
      }
    ],
    unlockReward: { type: "title", key: "challenger", name: "Challenger" }
  },
  {
    chapter: 2,
    name: "Risk & Reward",
    description: "Master modifiers and push your luck.",
    levels: [
      {
        chapter: 2,
        level: 1,
        botCount: 2,
        botDifficulty: "medium",
        targetScore: 150,
        variant: "standard",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "score-round", value: 40, description: "Score 40+ in one round" },
          { type: "flip7", description: "Achieve a Flip 7" }
        ]
      },
      {
        chapter: 2,
        level: 2,
        botCount: 2,
        botDifficulty: "medium",
        targetScore: 200,
        variant: "standard",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "score-total", value: 210, description: "Finish with 210+ points" },
          { type: "no-bust", description: "Win without busting" }
        ]
      },
      {
        chapter: 2,
        level: 3,
        botCount: 3,
        botDifficulty: "medium",
        targetScore: 150,
        variant: "standard",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "score-round", value: 50, description: "Score 50+ in one round" },
          { type: "win-streak", value: 2, description: "Win 2 rounds in a row" }
        ]
      },
      {
        chapter: 2,
        level: 4,
        botCount: 2,
        botDifficulty: "hard",
        targetScore: 200,
        variant: "standard",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "flip7", description: "Achieve a Flip 7" },
          { type: "no-bust", description: "Win without busting" }
        ]
      },
      {
        chapter: 2,
        level: 5,
        botCount: 3,
        botDifficulty: "hard",
        targetScore: 200,
        variant: "standard",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "score-total", value: 220, description: "Finish with 220+ points" },
          { type: "flip7", description: "Achieve a Flip 7" }
        ]
      },
      {
        chapter: 2,
        level: 6,
        botCount: 1,
        botDifficulty: "boss",
        targetScore: 200,
        variant: "standard",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "score-round", value: 60, description: "Score 60+ in one round" },
          { type: "no-bust", description: "Win without busting" }
        ]
      }
    ],
    unlockReward: { type: "title", key: "strategist", name: "Strategist" }
  },
  {
    chapter: 3,
    name: "Survival",
    description: "Endurance challenges against tough opponents.",
    levels: [
      {
        chapter: 3,
        level: 1,
        botCount: 3,
        botDifficulty: "hard",
        targetScore: 250,
        variant: "standard",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "no-bust", description: "Win without busting" },
          { type: "win-streak", value: 3, description: "Win 3 rounds in a row" }
        ]
      },
      {
        chapter: 3,
        level: 2,
        botCount: 2,
        botDifficulty: "hard",
        targetScore: 200,
        variant: "vengeance",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "score-total", value: 220, description: "Finish with 220+ points" },
          { type: "flip7", description: "Achieve a Flip 7" }
        ]
      },
      {
        chapter: 3,
        level: 3,
        botCount: 3,
        botDifficulty: "hard",
        targetScore: 250,
        variant: "vengeance",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "score-round", value: 50, description: "Score 50+ in one round" },
          { type: "no-bust", description: "Win without busting" }
        ]
      },
      {
        chapter: 3,
        level: 4,
        botCount: 2,
        botDifficulty: "boss",
        targetScore: 250,
        variant: "vengeance",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "flip7", description: "Achieve a Flip 7" },
          { type: "win-streak", value: 2, description: "Win 2 rounds in a row" }
        ]
      },
      {
        chapter: 3,
        level: 5,
        botCount: 3,
        botDifficulty: "boss",
        targetScore: 300,
        variant: "vengeance",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "score-total", value: 310, description: "Finish with 310+ points" },
          { type: "no-bust", description: "Win without busting" }
        ]
      },
      {
        chapter: 3,
        level: 6,
        botCount: 1,
        botDifficulty: "boss",
        targetScore: 200,
        variant: "vengeance-brutal",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "score-round", value: 60, description: "Score 60+ in one round" },
          { type: "flip7", description: "Achieve a Flip 7" }
        ]
      }
    ],
    unlockReward: { type: "title", key: "survivor", name: "Survivor" }
  },
  {
    chapter: 4,
    name: "Vengeance Awakens",
    description: "Master the vengeance cards: swap, steal, and discard.",
    levels: [
      {
        chapter: 4,
        level: 1,
        botCount: 1,
        botDifficulty: "medium",
        targetScore: 150,
        variant: "vengeance",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "score-total", value: 160, description: "Finish with 160+ points" },
          { type: "flip7", description: "Achieve a Flip 7" }
        ]
      },
      {
        chapter: 4,
        level: 2,
        botCount: 2,
        botDifficulty: "medium",
        targetScore: 150,
        variant: "vengeance",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "score-round", value: 40, description: "Score 40+ in one round" },
          { type: "no-bust", description: "Win without busting" }
        ]
      },
      {
        chapter: 4,
        level: 3,
        botCount: 2,
        botDifficulty: "hard",
        targetScore: 200,
        variant: "vengeance",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "score-total", value: 210, description: "Finish with 210+ points" },
          { type: "win-streak", value: 2, description: "Win 2 rounds in a row" }
        ]
      },
      {
        chapter: 4,
        level: 4,
        botCount: 3,
        botDifficulty: "hard",
        targetScore: 200,
        variant: "vengeance",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "flip7", description: "Achieve a Flip 7" },
          { type: "no-bust", description: "Win without busting" }
        ]
      },
      {
        chapter: 4,
        level: 5,
        botCount: 2,
        botDifficulty: "hard",
        targetScore: 250,
        variant: "vengeance",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "score-round", value: 50, description: "Score 50+ in one round" },
          { type: "win-streak", value: 2, description: "Win 2 rounds in a row" }
        ]
      },
      {
        chapter: 4,
        level: 6,
        botCount: 1,
        botDifficulty: "boss",
        targetScore: 200,
        variant: "vengeance",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "score-total", value: 220, description: "Finish with 220+ points" },
          { type: "flip7", description: "Achieve a Flip 7" }
        ]
      }
    ],
    unlockReward: { type: "title", key: "vengeful", name: "Vengeful" }
  },
  {
    chapter: 5,
    name: "The Gauntlet",
    description: "Face the toughest opponents in brutal conditions.",
    levels: [
      {
        chapter: 5,
        level: 1,
        botCount: 3,
        botDifficulty: "hard",
        targetScore: 250,
        variant: "vengeance",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "no-bust", description: "Win without busting" },
          { type: "win-streak", value: 3, description: "Win 3 rounds in a row" }
        ]
      },
      {
        chapter: 5,
        level: 2,
        botCount: 2,
        botDifficulty: "boss",
        targetScore: 250,
        variant: "vengeance",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "score-total", value: 260, description: "Finish with 260+ points" },
          { type: "flip7", description: "Achieve a Flip 7" }
        ]
      },
      {
        chapter: 5,
        level: 3,
        botCount: 3,
        botDifficulty: "boss",
        targetScore: 250,
        variant: "vengeance",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "score-round", value: 60, description: "Score 60+ in one round" },
          { type: "no-bust", description: "Win without busting" }
        ]
      },
      {
        chapter: 5,
        level: 4,
        botCount: 2,
        botDifficulty: "boss",
        targetScore: 200,
        variant: "vengeance-brutal",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "flip7", description: "Achieve a Flip 7" },
          { type: "win-streak", value: 2, description: "Win 2 rounds in a row" }
        ]
      },
      {
        chapter: 5,
        level: 5,
        botCount: 3,
        botDifficulty: "boss",
        targetScore: 300,
        variant: "vengeance-brutal",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "score-total", value: 310, description: "Finish with 310+ points" },
          { type: "no-bust", description: "Win without busting" }
        ]
      },
      {
        chapter: 5,
        level: 6,
        botCount: 3,
        botDifficulty: "boss",
        targetScore: 350,
        variant: "vengeance-brutal",
        starConditions: [
          { type: "win", description: "Win the game" },
          { type: "score-round", value: 70, description: "Score 70+ in one round" },
          { type: "flip7", description: "Achieve a Flip 7" }
        ]
      }
    ],
    unlockReward: { type: "title", key: "champion", name: "Champion" }
  }
];
var TOTAL_STORY_LEVELS = STORY_CHAPTERS.reduce((sum, ch) => sum + ch.levels.length, 0);
var STAR_COIN_REWARD = 10;
var DAILY_CHALLENGE_COIN_REWARD = 25;
var DAILY_CHALLENGE_WIN_BONUS = 50;

// src/constants/avatar.ts
var SKIN_PALETTES = [
  { base: "#FDDBB4", outline: "#D4A373", shadow: "#C08B5C" },
  // Light
  { base: "#F0C8A0", outline: "#C4956A", shadow: "#A67B55" },
  // Medium-light
  { base: "#D4A76A", outline: "#9E7B4E", shadow: "#7D5F3B" },
  // Medium
  { base: "#C08B5C", outline: "#8B6B42", shadow: "#6B4F30" },
  // Medium-dark
  { base: "#8D5524", outline: "#6B3F1A", shadow: "#4A2C12" },
  // Dark
  { base: "#5C3310", outline: "#3D220A", shadow: "#2A1808" }
  // Very dark
];
var HAIR_COLORS = [
  "#2C1B0E",
  // Black
  "#5C3317",
  // Dark brown
  "#8B6914",
  // Brown
  "#C4A35A",
  // Dirty blonde
  "#F0D58C",
  // Blonde
  "#CC4400",
  // Red
  "#808080",
  // Grey
  "#E8E8E8"
  // White
];
var HAIR_STYLES = [
  "Buzz",
  "Short",
  "Medium",
  "Long",
  "Curly",
  "Mohawk",
  "Ponytail",
  "Pigtails",
  "Bald",
  "Messy"
];
var EYE_STYLES = ["Round", "Almond", "Wide", "Narrow", "Wink", "Closed"];
var MOUTH_STYLES = ["Smile", "Neutral", "Open", "Grin", "Smirk"];
var HAT_STYLES = ["None", "Cap", "Beanie", "Top Hat", "Crown", "Bandana", "Pirate", "Wizard", "Cowboy"];
var GLASSES_STYLES = ["None", "Round", "Square", "Aviator", "Monocle", "Sunglasses"];
var FACIAL_HAIR_STYLES = ["None", "Stubble", "Mustache", "Beard", "Goatee"];
var AVATAR_PARTS = [
  // All skins free
  ...Array.from({ length: 6 }, (_, i) => ({ category: "skin", index: i, name: `Skin ${i + 1}`, unlockSource: "free" })),
  // Hair styles: first 5 free, rest shop
  ...HAIR_STYLES.map((name, i) => ({
    category: "hairStyle",
    index: i,
    name,
    unlockSource: i < 5 ? "free" : "shop",
    shopPrice: i >= 5 ? 50 : void 0
  })),
  // Hair colors: all free
  ...HAIR_COLORS.map((_, i) => ({ category: "hairColor", index: i, name: `Color ${i + 1}`, unlockSource: "free" })),
  // Eyes: first 4 free
  ...EYE_STYLES.map((name, i) => ({
    category: "eyes",
    index: i,
    name,
    unlockSource: i < 4 ? "free" : "shop",
    shopPrice: i >= 4 ? 30 : void 0
  })),
  // Mouths: all free
  ...MOUTH_STYLES.map((name, i) => ({ category: "mouth", index: i, name, unlockSource: "free" })),
  // Hats: first is "none", next 3 free, rest shop/story
  ...HAT_STYLES.slice(1).map((name, i) => ({
    category: "hat",
    index: i,
    name,
    unlockSource: i < 3 ? "free" : i < 6 ? "shop" : "story",
    shopPrice: i >= 3 && i < 6 ? 75 : void 0,
    storyRequirement: i >= 6 ? `chapter_${i - 4}` : void 0
  })),
  // Glasses: first is "none", next 2 free, rest shop
  ...GLASSES_STYLES.slice(1).map((name, i) => ({
    category: "glasses",
    index: i,
    name,
    unlockSource: i < 2 ? "free" : "shop",
    shopPrice: i >= 2 ? 50 : void 0
  })),
  // Facial hair: first is "none", all free
  ...FACIAL_HAIR_STYLES.slice(1).map((name, i) => ({
    category: "facialHair",
    index: i,
    name,
    unlockSource: "free"
  }))
];
function randomAvatarConfig() {
  return {
    skin: Math.floor(Math.random() * 6),
    hairStyle: Math.floor(Math.random() * 5),
    // Only free styles
    hairColor: Math.floor(Math.random() * 8),
    eyes: Math.floor(Math.random() * 4),
    mouth: Math.floor(Math.random() * 5),
    hat: null,
    glasses: null,
    facialHair: null,
    border: null
  };
}
function isValidAvatarConfig(config) {
  if (!config || typeof config !== "object") return false;
  const c = config;
  return typeof c.skin === "number" && c.skin >= 0 && c.skin <= 5 && typeof c.hairStyle === "number" && c.hairStyle >= 0 && c.hairStyle <= 9 && typeof c.hairColor === "number" && c.hairColor >= 0 && c.hairColor <= 7 && typeof c.eyes === "number" && c.eyes >= 0 && c.eyes <= 5 && typeof c.mouth === "number" && c.mouth >= 0 && c.mouth <= 4 && (c.hat === null || typeof c.hat === "number" && c.hat >= 0 && c.hat <= 7) && (c.glasses === null || typeof c.glasses === "number" && c.glasses >= 0 && c.glasses <= 4) && (c.facialHair === null || typeof c.facialHair === "number" && c.facialHair >= 0 && c.facialHair <= 3) && (c.border === null || typeof c.border === "string");
}
export {
  ACHIEVEMENT_DEFINITIONS,
  ACHIEVEMENT_MAP,
  ACHIEVEMENT_TIERS,
  ACTION_CARD_COLORS,
  ACTION_CARD_COUNTS,
  ACTION_CARD_ICONS,
  ACTION_CARD_VALUES,
  ALL_CARD_KEYS,
  ALL_STANDARD_CARD_KEYS,
  ALL_VENGEANCE_CARD_KEYS,
  AVATAR_PARTS,
  BOT_DECISION_DELAY_MAX,
  BOT_DECISION_DELAY_MIN,
  BOT_NAMES,
  CHAT_HISTORY_SIZE,
  CHAT_MAX_LENGTH,
  CHAT_RATE_LIMIT_MS,
  COIN_REWARDS,
  DAILY_CHALLENGE_COIN_REWARD,
  DAILY_CHALLENGE_WIN_BONUS,
  DEAL_DELAY_MS,
  DEFAULT_REACTION_PRESETS,
  DEFAULT_SOUNDBITE_PRESETS,
  DEFAULT_TARGET_SCORE,
  DIV2_MODIFIER_COLOR,
  DUEL_ACTION_CARDS,
  DUEL_BEST_OF,
  DUEL_CARD_DEFINITIONS,
  DUEL_MAX_ACTIONS,
  DUEL_MAX_PERKS,
  DUEL_PACKS,
  DUEL_PERK_CARDS,
  DUEL_TARGET_SCORE,
  EVENTS,
  EYE_STYLES,
  FACIAL_HAIR_STYLES,
  FLIP_7_BONUS,
  FLIP_7_THRESHOLD,
  FREE_AVATARS,
  GLASSES_STYLES,
  GROUP_SLUG_MAX_LENGTH,
  GROUP_SLUG_PATTERN,
  HAIR_COLORS,
  HAIR_STYLES,
  HAT_STYLES,
  HS_BETTING_TIMER_MS,
  HS_BURN_CARD_COUNT,
  HS_CARDS_SUBMITTED_BY_PLAYER_COUNT,
  HS_CARD_FLIP_INTERVAL_MS,
  HS_CARD_SUBMIT_TIMER_MS,
  HS_CARD_TEMPLATES,
  HS_DOUBLE_DOWN_TIMER_MS,
  HS_FOLD_ANIMATION_MS,
  HS_HAND_SIZE_BY_PLAYER_COUNT,
  HS_LANES,
  HS_LIFE_OUTCOMES,
  HS_MASCOTS,
  HS_MASCOT_IDS,
  HS_MASCOT_TICKET_PAYOUTS,
  HS_MAX_PLAYERS,
  HS_MIN_PLAYERS,
  HS_RACE_COUNTDOWN_MS,
  HS_RACE_RESULTS_DISPLAY_MS,
  HS_RANDOM_CARDS_BY_PLAYER_COUNT,
  HS_SIDE_BETS_PER_RACE,
  HS_SIDE_BET_PAYOUT,
  HS_SIDE_BET_QUESTIONS,
  HS_STARTING_CARD_TEMPLATES,
  HS_STARTING_MONEY,
  HS_TICKETS_PER_PLAYER_BY_PLAYER_COUNT,
  HS_TOTAL_RACES,
  HS_TOTAL_RACE_DECK_SIZE,
  HS_TRACK_CONFIG,
  MAX_BOTS,
  MAX_PLAYERS,
  MAX_SPECTATORS,
  MAX_VISIBLE_TOASTS,
  MAX_VOICE_MEMO_SIZE,
  MIN_PLAYERS,
  MODIFIER_CARD_COLOR,
  MODIFIER_CARD_COUNTS,
  MODIFIER_CARD_VALUES,
  MOUTH_STYLES,
  NEGATIVE_MODIFIER_COLOR,
  NUMBER_CARD_COLORS,
  NUMBER_CARD_COUNTS,
  NUMBER_CARD_NAMES,
  NUMBER_CARD_VALUES,
  PLAYER_NAME_MAX_LENGTH,
  PREMIUM_SOUNDBITE_KEYS,
  REACTION_COOLDOWN_MS,
  REACTION_TOAST_DURATION_MS,
  REVEAL_DELAY_MS,
  ROOM_EXPIRY_MS,
  SHINY_CHANCE,
  SHINY_EVENT,
  SHINY_ITEM_PREFIX,
  SHINY_TIERS,
  SHINY_TIER_ORDER,
  SHOP_ITEMS,
  SHOP_ITEM_MAP,
  SKIN_PALETTES,
  SOUNDBITE_COOLDOWN_MS,
  SOUNDBITE_PACKS,
  SOUNDBITE_PACK_UNLOCK,
  SPECTATOR_ONLY_EXPIRY_MS,
  STARTER_PACK,
  STAR_COIN_REWARD,
  STORY_CHAPTERS,
  TABLE_TIERS,
  TARGET_SCORE_MAX,
  TARGET_SCORE_MIN,
  THEME_DEFINITIONS,
  THEME_MAP,
  TOTAL_DECK_SIZE,
  TOTAL_STORY_LEVELS,
  VENGEANCE_ACTION_CARD_COUNTS,
  VENGEANCE_ACTION_CARD_VALUES,
  VENGEANCE_DECK_SIZE,
  VENGEANCE_MODIFIER_CARD_COUNTS,
  VENGEANCE_MODIFIER_CARD_VALUES,
  VENGEANCE_SPECIAL_NUMBER_CARDS,
  VOICE_MEMO_MAX_SECONDS,
  buildBettingTickets,
  buildDeck,
  calculateScore,
  cardKeyToCard,
  countUniqueNumbers,
  drawCard,
  findDuplicateNumber,
  getCardKey,
  getLifeOutcome,
  getShinyChance,
  hasSecondChance,
  highestTier,
  isActionCard,
  isModifierCard,
  isNegativeModifier,
  isNumberCard,
  isSoundbiteOwned,
  isValidAvatarConfig,
  isVengeanceCard,
  parseShinyItemKey,
  pickSideBetQuestions,
  randomAvatarConfig,
  rollShinyTier,
  seededShuffle,
  shinyItemKey,
  shuffleDeck
};
//# sourceMappingURL=index.mjs.map